<script setup lang="ts">
import { APP_NAME, getPageTitle } from "@/assets/js/appConfig";
import { useDefaultStore } from "@/store";

;

type TPeopleRouteName =
	| "PeopleClassicGamePage"
	| "PeopleBeatTheClockPage"
	| null;

type TPeopleGameMode = {
	title: string;
	description: string;
	icon: string;
	routeName: TPeopleRouteName;
	disabled: boolean;
	isMultiplayer?: boolean;
	scores?: {
		best: number;
		last: number;
	};
};

const store = useDefaultStore();

const classicBestScore = computed(
	() => store.userData?.usr_people_classic_best_score ?? 0,
);
const classicLastScore = computed(
	() => store.userData?.usr_people_classic_last_score ?? 0,
);
const btcBestScore = computed(
	() => store.userData?.usr_people_btc_best_score ?? 0,
);
const btcLastScore = computed(
	() => store.userData?.usr_people_btc_last_score ?? 0,
);

const gameModes = computed<TPeopleGameMode[]>(() => [
	{
		title: "Classic",
		description: "Guess which person was born earlier.",
		icon: "👤",
		routeName: "PeopleClassicGamePage",
		disabled: false,
		scores: {
			best: classicBestScore.value,
			last: classicLastScore.value,
		},
	},
	{
		title: "Beat the Clock",
		description: "Build your score before time runs out.",
		icon: "⏱️",
		routeName: "PeopleBeatTheClockPage",
		disabled: false,
		scores: {
			best: btcBestScore.value,
			last: btcLastScore.value,
		},
	},
	{
		title: "Play with Others",
		description: "Challenge friends and compete together.",
		icon: "👥",
		routeName: null,
		disabled: false,
		isMultiplayer: true,
	},
]);

useHead({
	title: getPageTitle("People"),
	meta: [
		{
			name: "title",
			content: getPageTitle("People"),
		},
		{
			name: "description",
			content:
				"Challenge yourself by guessing which person was born earlier.",
		},
		{ name: "robots", content: "index, follow" },
		{ property: "og:title", content: getPageTitle("People") },
		{
			property: "og:description",
			content:
				"Challenge yourself by guessing which person was born earlier.",
		},
		{ property: "og:site_name", content: APP_NAME },
		{
			name: "viewport",
			content: "width=device-width, initial-scale=1, viewport-fit=cover",
		},
	],
});
</script>

<template>
	<CategoryPageComp
		:category-code="store.MAIN_CATEGORIES.PEOPLE"
		category-name="People"
		fallback-route-name="PeoplePage"
		:game-modes="gameModes"
		route-target-settings="PeopleSettingsPage"
		route-target-leaderboard="PeopleLeaderboard"
	/>
</template>
